import type { useTranslations } from "next-intl";
import {
  EMAIL_RULE_REASON_KEYS,
  emailRuleReasonFromDetail,
  emailRuleTextFor,
} from "./emailRuleMessage";

type Translator = ReturnType<typeof useTranslations>;

/**
 * Anything carrying a backend error code on `.message` — a caught `Error`
 * (rethrowApi puts the i18n key there) or a `ServerError` from a handler.
 * `detail` is the raw human-readable backend message that rode alongside the
 * code (attached by rethrowApi / handleErrorServer), English-only.
 */
type BackendErrorLike = {
  message?: unknown;
  detail?: unknown;
};

/**
 * Codes that are catch-alls: their translation ("Validation error") names no
 * field, so the raw backend `detail` is appended — mixed-language beats a
 * message that tells the user nothing. Specific codes keep their (already
 * precise) translation and never get the English detail glued on.
 */
const GENERIC_CODES = new Set(["validationError"]);

/**
 * Field-gate v2 (main-backend `GuestQueryFieldGate`): the two refusals a role
 * meets when it acts on, or groups by, a column it may not see.
 *
 *   - `filterKeyForbidden` — a bulk action / export / send on "all matching"
 *     narrows on such a column; refused, nothing was changed.
 *   - `groupByNotAllowed` — a grouping or bucket refused there: on such a
 *     column, or on one that is not groupable on that surface (any role).
 *
 * Both are thrown as `code:<key>`. `ErrorFormater` resolves the bare code and
 * the raw text rides along as the envelope's `message` (our `detail`), so the
 * usual shape is already the code. This exists for the surfaces that read
 * `Error.message` alone and would otherwise print `filterKeyForbidden:email`
 * — or, worse, "please try again", which hides that retrying cannot help.
 */
const FIELD_GATE_CODES = ["filterKeyForbidden", "groupByNotAllowed"] as const;
export type FieldGateCode = (typeof FIELD_GATE_CODES)[number];

/**
 * The bare field-gate code of `raw` — the code itself or any `code:…` form
 * (`code:<key>`, the Bean-Validation `code: <prose>`). Null for anything else,
 * including a longer code that merely starts with the name.
 */
export function fieldGateCode(raw: unknown): FieldGateCode | null {
  if (typeof raw !== "string") return null;
  for (const code of FIELD_GATE_CODES) {
    if (raw === code || raw.startsWith(`${code}:`)) return code;
  }
  return null;
}

export type TranslateBackendErrorOptions = {
  /**
   * The organiser's word for a guest column named by its wire key
   * (`guestKeyLabel` over the role's VISIBLE catalogue), or `null` when the
   * role may not be told — then the sentence carries no suffix at all, not
   * even the raw key. Applied to the detail of the field-gate codes only —
   * there the suffix IS a column; every other code's suffix (a rule id, a
   * view, a key list) stays verbatim.
   */
  fieldLabel?: (key: string) => string | null;
};

/**
 * Codes whose detail suffix is a guest COLUMN: the two field-gate refusals and
 * the dashboard card's own sentence for the same refusal.
 */
const COLUMN_DETAIL_CODES: ReadonlySet<string> = new Set([
  ...FIELD_GATE_CODES,
  "cardFieldNotReadable",
]);

/**
 * " — <suffix>" for a refusal's detail.
 *
 * For a column-naming code the column is said only by a caller that knows what
 * the role may be told (`fieldLabel`), and only when that resolver answers:
 * without one there is NO suffix, never the raw key — a hidden custom field's
 * key is its slug, and the slug names it (round-2 decision). Every other code
 * keeps its suffix verbatim (a rule id, a view, a key list).
 */
function detailSuffix(
  code: string,
  suffix: string,
  options: TranslateBackendErrorOptions | undefined,
): string {
  if (!COLUMN_DETAIL_CODES.has(code)) return ` — ${suffix}`;
  const label = options?.fieldLabel ? options.fieldLabel(suffix) : null;
  return label ? ` — ${label}` : "";
}

/**
 * Translate a backend error code through next-intl's `errors` namespace, with
 * a graceful fallback chain for codes the i18n bundle hasn't caught up with.
 *
 *   1. If the code exists in the namespace, return the translation — for
 *      generic catch-all codes (see {@link GENERIC_CODES}) with a `detail`,
 *      append the raw backend text so the field-level reason isn't lost.
 *   2. Otherwise log a one-line warn (so the missing key surfaces in DevTools)
 *      and return either the caller-supplied `fallback` (already-translated
 *      text), the raw backend `detail`, the namespace's `unknown` translation,
 *      or the raw code as a last resort.
 *
 * Pass an already-bound translator from `useTranslations("errors")`. The
 * second argument accepts the plain code string (legacy call shape, no detail
 * available) or the whole error object (`ServerError` / caught `Error`) —
 * pass the OBJECT wherever you have one so `detail` travels. Callers that
 * need the fallback to come from a DIFFERENT namespace (e.g. a
 * domain-specific message like t("statsFailedToLoad")) translate it
 * themselves and pass the resulting string as the third argument.
 *
 * `unknown` is present in every cockpit locale bundle (verified scripted) so
 * the second-tier fallback is safe.
 */
export function translateBackendError(
  t: Translator,
  error: string | BackendErrorLike | null | undefined,
  fallback?: string,
  options?: TranslateBackendErrorOptions,
): string {
  const code =
    typeof error === "string"
      ? error
      : error && typeof error.message === "string" && error.message.length > 0
        ? error.message
        : null;
  const detail =
    error &&
    typeof error === "object" &&
    typeof error.detail === "string" &&
    error.detail.length > 0 &&
    error.detail !== code
      ? error.detail
      : undefined;

  if (!code) {
    if (fallback !== undefined) return fallback;
    return t.has("unknown") ? t("unknown") : "";
  }
  // Plan "Saubere Eingaben": the server refuses an undeliverable address with
  // `emailInvalid` and the rule's reason as detail — bare (`invisible`), in
  // the constraint template's `emailInvalid:<reason>` form, and for a list
  // element with `;index=<n>` behind it (`parseEmailRuleDetail`). The reason
  // has its own line; the server names no character, so the line is asked
  // for its no-character branch. A reason the bundle does not know, or
  // `shape`, is the plain `emailInvalid` line — never "Invalid email address
  // — bogus".
  if (code === "emailInvalid" && detail) {
    const reason = emailRuleReasonFromDetail(detail);
    if (reason && t.has(EMAIL_RULE_REASON_KEYS[reason])) {
      return emailRuleTextFor(t, reason, null);
    }
    if (t.has(code)) return t(code);
  }
  // PB-CDP: a dissolve door's `activeSubscription:<REASON>[:<epochSec>]`
  // (`RUNNING`, `UNKNOWN`, `CANCELLING:1793404800`). The places that can say
  // the reason read it through `reasonTextFromError`
  // (`components/PDeleteCompany/dissolveText`); everyone else gets the bare
  // sentence — the suffix is a machine token, not a rule id, and "… —
  // CANCELLING:1793404800" told nobody anything.
  if (code === "activeSubscription" && t.has(code)) return t(code);
  if (t.has(code)) {
    const translated = t(code);
    if (detail && GENERIC_CODES.has(code)) {
      // A catch-all whose detail is itself a SUFFIXED code the bundle knows
      // (`displayConfigCueLabelInvalid:<id>` from a backend that has not yet
      // learned to split it): translate the real code and keep the suffix —
      // "Validation error — displayConfigCueLabelInvalid:9f1c…" told nobody
      // anything.
      const suffixed = splitSuffixedCode(detail);
      if (suffixed && t.has(suffixed.code)) {
        return `${t(suffixed.code)}${detailSuffix(suffixed.code, suffixed.suffix, options)}`;
      }
      // `validationError: <code>` — the request validators' own shape (the
      // send-class gate throws `validationError: sendClass`). When the bundle
      // knows the bare code it IS the precise message; the English detail
      // would only repeat it untranslated. Same rule as translateJobError.
      const bare = /^validationError:\s*([a-z][A-Za-z0-9]*)\s*$/.exec(detail)?.[1];
      if (bare && t.has(bare)) {
        return t(bare);
      }
      return `${translated} — ${detail}`;
    }
    // A specific code whose detail is the raw `code:suffix` the backend threw
    // (the suffix names the rule / view / key): append just the suffix — for
    // a field-gate code by the column's label when the caller can name it.
    const suffixed = detail ? splitSuffixedCode(detail) : null;
    if (suffixed && suffixed.code === code) {
      return `${translated}${detailSuffix(code, suffixed.suffix, options)}`;
    }
    return translated;
  }
  // A field-gate refusal whose raw `code:<key>` arrived AS the code (a path
  // that kept only the text): the same answer as the `{code, detail}` shape.
  const gate = fieldGateCode(code);
  if (gate && gate !== code && t.has(gate)) {
    return translateBackendError(t, { message: gate, detail: code }, fallback, options);
  }
  if (typeof console !== "undefined" && typeof console.warn === "function") {
    console.warn(`[i18n] missing key for backend error code: ${code}`);
  }
  // A code the bundle has not caught up with, whose detail is the raw
  // `code:suffix` a validator threw: the suffix names the view / key / rule
  // and is the only thing that tells the operator what to fix. A generic
  // fallback ("could not save") would throw exactly that away.
  if (detail && splitSuffixedCode(detail) && t.has("validationError")) {
    return `${t("validationError")} — ${detail}`;
  }
  if (fallback !== undefined) return fallback;
  if (detail) return detail;
  if (t.has("unknown")) return t("unknown");
  return code;
}

/**
 * `code:suffix` as main-backend's validators throw it (`displayConfigFieldKeyInvalid:guestList:cues`,
 * `displayConfigCueLabelInvalid:<rule id>`): the bare code before the first
 * colon, everything after it verbatim. Null for anything else.
 */
export function splitSuffixedCode(raw: string): { code: string; suffix: string } | null {
  // `\S` right after the colon, as main-backend's own arm requires: a
  // Bean-Validation sentence (`message: size must be…`) is prose, not a code.
  const m = /^([a-z][A-Za-z0-9]*):(\S.*)$/s.exec(raw);
  if (!m || m[2]!.trim().length === 0) return null;
  return { code: m[1]!, suffix: m[2]!.trim() };
}

/** `emailQuotaExceeded` bare or with the consumer's `: needed N[, remaining M]`. */
const EMAIL_QUOTA_RE = /^emailQuotaExceeded(?::|$)/;

/**
 * Which `errors.*` sentence a refused send gets (audit purchased emails LOW-2,
 * Nikita 2026-10-06): "… upgrade your plan or buy an event licence" only when
 * the company CAN buy one (`useCanBuyEventLicence`); a company on the top tier
 * has nothing left to buy, an event of ANOTHER company cannot be bought for
 * from this login, anyone but the company's OWNER can neither upgrade nor
 * buy, and an unknown answer (still loading, a failed read) must not promise
 * a purchase either — all of them get "… please contact support".
 */
export function emailQuotaExceededKey(
  canBuyEventLicence: boolean | null | undefined,
): "emailQuotaExceeded" | "emailQuotaExceededNoLicence" {
  return canBuyEventLicence === true ? "emailQuotaExceeded" : "emailQuotaExceededNoLicence";
}

/**
 * The English the job producers wrote into `errorMessage` before they sent
 * codes (main-consumer bulk-email-fanout / scheduled-workflow-fanout,
 * main-backend `queueOnlyFailureMessage`). Jobs keep these strings in Mongo
 * after the producers switch, so the map stays for good. Each entry: does
 * this raw string match, and which `errors.*` key says it. `taskType` decides
 * the one string that means two things ("Queue producer failed:" is a send
 * that never started for SEND_EMAILS, any other queue-only task otherwise).
 * Fixture: src/lib/i18n/__tests__/__fixtures__/jobErrorLegacyStrings.json.
 */
const LEGACY_JOB_ERRORS: ReadonlyArray<{
  matches: (raw: string) => boolean;
  key: (taskType: string | undefined) => string;
}> = [
  { matches: (r) => r === "invalidDomain: dmarcReject", key: () => "senderDomainDmarcReject" },
  { matches: (r) => r.startsWith("invalidDomain:"), key: () => "senderDomainNotSendable" },
  { matches: (r) => r === "no guests with email match the filter", key: () => "noRecipientsWithEmail" },
  {
    matches: (r) => r.startsWith("Queue producer failed:"),
    key: (taskType) => (taskType === "SEND_EMAILS" ? "sendCouldNotStart" : "jobFailed"),
  },
  {
    matches: (r) => r === "scheduled workflow has no SEND_EMAIL template",
    key: () => "workflowHasNoEmailTemplate",
  },
  // "event missing companyId", "event missing, archived, or has no companyId"
  { matches: (r) => r.startsWith("event missing"), key: () => "jobFailed" },
];

/** `<code>:` / `<code>: …` — a producer's code with a suffix it appended. */
const PREFIXED_CODE_RE = /^([a-z][A-Za-z0-9]*):/;

/** next-intl's `has` reads the prototype chain: `has("toString")` is true. */
function isPrototypeName(key: string): boolean {
  return Object.prototype.hasOwnProperty.call(Object.prototype, key);
}

export type JobErrorText = {
  /** The line to show. */
  text: string;
  /**
   * The producer's raw text, for a collapsed detail under the line — set
   * whenever the line is NOT the producer's own code (a legacy map hit, the
   * generic fallback): support needs the original, the user needs the
   * sentence. `null` when the code itself was translated.
   */
  raw: string | null;
};

/**
 * Translate a background JOB's stored `errorMessage` — the line plus, where
 * the line is not the producer's own code, the raw text for a detail.
 *
 * Job errors are not request errors: they were written by a worker (or by the
 * executor fallback) into Mongo, minutes ago, in no particular locale, and the
 * renderer looks them up by EXACT key. That works for a bare code like
 * `eventAtCapacity` and fails for every parameterised one — `tooManyGroups`
 * has been fully translated in all 57 bundles the whole time and never once
 * displayed, because the stored message reads
 * `validationError: tooManyGroups (207 > 200)` and no key matches that.
 *
 * Order: the producers' legacy English ({@link LEGACY_JOB_ERRORS}) → the
 * consumer's quota refusal (`emailQuotaExceeded: needed N…`, its sentence
 * picked by `emailQuotaExceededKey`) → exact hit → `validationError: <code>`
 * with an optional count → a `<code>:` prefix the bundle knows → the generic
 * `errors.jobFailed` (the raw string only when even that key is missing). The
 * count is appended in code rather than passed as an ICU argument: a producer
 * that emits none would otherwise make next-intl throw and take the whole job
 * row down with it. Plan graceful-giggling-melody M-29: the row used to show
 * whatever English the worker wrote.
 */
export function describeJobError(
  t: Translator,
  errorMessage: string | null | undefined,
  opts?: { canBuyEventLicence?: boolean | null; taskType?: string },
): JobErrorText {
  if (!errorMessage) return { text: "", raw: null };
  const legacy = LEGACY_JOB_ERRORS.find((e) => e.matches(errorMessage));
  if (legacy) {
    const key = legacy.key(opts?.taskType);
    if (t.has(key)) return { text: t(key), raw: errorMessage };
  }
  // main-consumer's quota refusal (`emailQuotaExceeded: needed N`, the
  // scheduled lane adds `, remaining M`): the sentence, never the raw token —
  // and the licence suggestion only for a company that can buy one.
  if (EMAIL_QUOTA_RE.test(errorMessage)) {
    const key = emailQuotaExceededKey(opts?.canBuyEventLicence);
    if (t.has(key)) return { text: t(key), raw: null };
  }
  if (!isPrototypeName(errorMessage) && t.has(errorMessage)) {
    return { text: t(errorMessage), raw: null };
  }
  const m = VALIDATION_CODE_RE.exec(errorMessage);
  if (m) {
    const code = m[1]!;
    if (!isPrototypeName(code) && t.has(code)) {
      const translated = t(code);
      return { text: m[2] ? `${translated} (${m[2]})` : translated, raw: null };
    }
  }
  const prefixed = PREFIXED_CODE_RE.exec(errorMessage)?.[1];
  if (prefixed && !isPrototypeName(prefixed) && t.has(prefixed)) {
    return { text: t(prefixed), raw: errorMessage };
  }
  if (t.has("jobFailed")) return { text: t("jobFailed"), raw: errorMessage };
  return { text: errorMessage, raw: null };
}

/** {@link describeJobError}'s line alone. */
export function translateJobError(
  t: Translator,
  errorMessage: string | null | undefined,
  opts?: { canBuyEventLicence?: boolean | null; taskType?: string },
): string {
  return describeJobError(t, errorMessage, opts).text;
}

/**
 * `validationError: <code>` as main-backend's request validators throw it
 * (`validationError: exportDisabled`, `validationError: paperSize`), with the
 * optional `(207 > 200)` count some producers append. The bare code is group
 * 1, the count group 2. Shared by the job-row and the submit-time paths.
 */
const VALIDATION_CODE_RE = /^validationError:\s*([a-z][A-Za-z0-9]*)(?:\s*\((\d+)[^)]*\))?/;

/**
 * Translate a SUBMIT-time `validationError: <code>` string.
 *
 * A 400 from a job-submit endpoint arrives as `{errorCode: "validationError",
 * message: "validationError: exportDisabled"}`; the error handler puts the
 * code into `message` and the raw string into `detail`. Rendering either
 * verbatim shows the operator the literal token in red. This reads the real
 * code out of the raw string: its own sentence when the bundle has one, the
 * generic "Validation error" when it does not — never the token.
 *
 * Returns null when the text is not a prefixed validation code at all, so the
 * caller can fall through to {@link translateBackendError} unchanged.
 */
export function translateValidationCode(
  t: Translator,
  text: string | null | undefined,
): string | null {
  if (!text) return null;
  const m = VALIDATION_CODE_RE.exec(text);
  if (!m) return null;
  const code = m[1]!;
  if (t.has(code)) return t(code);
  return t.has("validationError") ? t("validationError") : null;
}

/**
 * One row of the guest batch-update partial-failure envelope. Structural on
 * purpose so this module stays free of the api/errors layer — `GuestUpdateFailure`
 * (see ErrorHandler.ts) is assignable to it.
 */
type GuestFailureLike = {
  errorCode?: unknown;
  message?: unknown;
  // Rows carry guestId/capacityContext too; this reads only the two above.
  [key: string]: unknown;
};

/** A caught Error / ServerError that may carry the per-guest failure rows. */
type BatchErrorLike = BackendErrorLike & { failures?: unknown };

/**
 * The dedupe key of a SPECIFIC code: the code — forty rows blocked by the same
 * rule are one sentence. `emailInvalid` is the exception: its message carries
 * the rule's reason (`emailInvalid:invisible`), each reason has its own line,
 * and "the reason per guest" is the batch door's contract — two guests
 * refused for different reasons are two sentences, not the first one's.
 *
 * The key is the LINE the row renders as, not the reason: `shape`, a row
 * without a reason and a reason this build (or this bundle) does not know all
 * read as the generic `emailInvalid` sentence, and keyed by reason that
 * sentence was printed once for each of them.
 */
function specificFailureKey(t: Translator, code: string, message: string): string {
  if (code !== "emailInvalid") return code;
  const reason = emailRuleReasonFromDetail(message);
  if (!reason) return code;
  const key = EMAIL_RULE_REASON_KEYS[reason];
  return t.has(key) ? key : code;
}

/**
 * At most this many distinct reasons are rendered. A 50-row batch that fails
 * for five different reasons must not paint a wall of text into the one-line
 * error slot the detail panel and the editing banner give us; the header
 * sentence already says that some rows failed.
 */
const MAX_FAILURE_LINES = 3;

/**
 * Turn the per-guest `failures[]` of a `batchUpdatePartialFailure` response
 * into human sentences, one per distinct reason.
 *
 * Each row is `{errorCode, message}` — i.e. exactly the `{message, detail}`
 * shape {@link translateBackendError} already handles, where the row's
 * `errorCode` is the code and its English `message` is the detail. Routing
 * through it is what makes a `validationError` row read as
 * "Validation error — Changing guestType is not supported" instead of a bare
 * "Validation error" that names nothing.
 *
 * Dedupe key: the code alone for specific codes, code + message for the
 * generic catch-alls. Two `validationError` rows with different messages are
 * two different problems and both must be shown; forty rows blocked by the
 * same specific rule are one sentence.
 */
export function describeGuestUpdateFailures(
  t: Translator,
  failures: ReadonlyArray<GuestFailureLike> | null | undefined,
): string[] {
  const seen = new Set<string>();
  const lines: string[] = [];
  for (const row of failures ?? []) {
    const code =
      typeof row?.errorCode === "string" && row.errorCode.length > 0
        ? row.errorCode
        : null;
    if (!code) continue;
    const message = typeof row.message === "string" ? row.message : "";
    const key = GENERIC_CODES.has(code) ? `${code}\u0000${message}` : specificFailureKey(t, code, message);
    if (seen.has(key)) continue;
    seen.add(key);
    lines.push(translateBackendError(t, { message: code, detail: message }));
    if (lines.length === MAX_FAILURE_LINES) break;
  }
  return lines;
}

/**
 * The single entry point for rendering a guest save failure.
 *
 * The batch-update 400 carries NO top-level `message`, so the envelope alone
 * ({@code batchUpdatePartialFailure}) is not a reason — and until the key
 * existed it rendered as the raw code. Read `failures[]` instead:
 *
 *   - no usable rows  → identical to {@link translateBackendError} (every
 *     non-batch error keeps its current behaviour byte for byte);
 *   - exactly one row → that reason alone, no header (the detail panel edits
 *     one guest; a "some guests failed" header above it is noise);
 *   - several rows    → the translated envelope header, then each reason.
 */
export function describeBatchUpdateError(
  t: Translator,
  // `unknown` on purpose: every call site is a `catch (error)` binding, and
  // forcing each one to cast would be the kind of ceremony that gets skipped.
  error: unknown,
  fallback?: string,
): string {
  const batch = error as BatchErrorLike | string | null | undefined;
  const failures =
    batch && typeof batch === "object" && Array.isArray(batch.failures)
      ? (batch.failures as ReadonlyArray<GuestFailureLike>)
      : undefined;
  const lines = describeGuestUpdateFailures(t, failures);
  if (lines.length === 0) return translateBackendError(t, batch, fallback);
  if (lines.length === 1) return lines[0];
  return [translateBackendError(t, batch, fallback), ...lines].join(" ");
}
